import { Controller, type Control } from 'react-hook-form'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { JobAcceptanceMode } from '@/types'

export type JobAcceptanceModeFormFields = {
  assignment_acceptance_mode?: JobAcceptanceMode
  workday_entry_acceptance_mode?: JobAcceptanceMode
  workday_entry_approval_mode?: JobAcceptanceMode
}

const ACCEPTANCE_MODE_FIELDS: Array<{
  name: keyof JobAcceptanceModeFormFields
  label: string
  description: string
}> = [
  {
    name: 'assignment_acceptance_mode',
    label: 'Assignment acceptance',
    description: 'Manual: worker must accept the assignment. Auto: accepted when a worker is assigned.',
  },
  {
    name: 'workday_entry_acceptance_mode',
    label: 'Workday entry acceptance',
    description: 'Manual: worker must accept the scheduled entry. Auto: entry is scheduled immediately.',
  },
  {
    name: 'workday_entry_approval_mode',
    label: 'Workday entry approval',
    description: 'Manual: supervisor approves completed entries. Auto: checkout finalizes the entry as approved.',
  },
]

type JobAcceptanceModeFieldsProps = {
  control: Control<JobAcceptanceModeFormFields>
}

export function bindJobAcceptanceModeFormFields<T extends JobAcceptanceModeFormFields>(form: {
  control: Control<T>
}): JobAcceptanceModeFieldsProps {
  return {
    control: form.control as unknown as Control<JobAcceptanceModeFormFields>,
  }
}

export function JobAcceptanceModeFields({ control }: JobAcceptanceModeFieldsProps) {
  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle className="text-base">Worker workflow</CardTitle>
        <CardDescription>
          Control how assignments, workday entries, and checkout approval are handled for this job.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-3">
        {ACCEPTANCE_MODE_FIELDS.map((field) => (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={field.name}>{field.label}</Label>
            <Controller
              control={control}
              name={field.name}
              render={({ field: formField }) => (
                <Select
                  value={formField.value || 'manual'}
                  onValueChange={formField.onChange}
                >
                  <SelectTrigger id={field.name}>
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="auto">Auto</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-xs text-muted-foreground">{field.description}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
